import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, FileImage, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '@/lib/image-utils';

interface SelectedImage {
  base64: string;
  mimeType: string;
  previewUrl?: string;
}

interface AIWizardUploadProps {
  onImagesChange: (images: { base64: string, mimeType: string }[]) => void;
  currentImages: { base64: string, mimeType: string }[];
}

export function AIWizardUpload({ onImagesChange, currentImages }: AIWizardUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImages, setLocalImages] = useState<SelectedImage[]>([]);
  const localImagesRef = useRef<SelectedImage[]>([]);
  const [isLocalProcessing, setIsLocalProcessing] = useState(false);

  // Max files limit
  const MAX_FILES = 10;

  // Track state in ref for safe cleanup on unmount
  useEffect(() => {
    localImagesRef.current = localImages;
  }, [localImages]);

  // Sync from parent (only if local is empty or parent cleared)
  useEffect(() => {
    if (currentImages.length === 0 && localImages.length > 0) {
      setLocalImages([]);
    } else if (currentImages.length > 0 && localImages.length === 0) {
      setLocalImages(currentImages.map(img => ({
        base64: img.base64,
        mimeType: img.mimeType
      })));
    }
  }, [currentImages]);

  // Handle Blob URL cleanup strictly on unmount
  useEffect(() => {
    return () => {
      // Use ref to get the absolute latest state at unmount time
      localImagesRef.current.forEach(img => {
        if (img.previewUrl) {
          try {
            URL.revokeObjectURL(img.previewUrl);
          } catch (e) {
            console.error('Error revoking URL:', e);
          }
        }
      });
    };
  }, []);

  const handleFileSelect = async (files: FileList) => {
    const newFiles = Array.from(files);

    if (localImages.length + newFiles.length > MAX_FILES) {
      toast.error(t('aiWizard.errorMaxFiles', { count: MAX_FILES }) || `Máximo de ${MAX_FILES} arquivos permitido`);
      return;
    }

    setIsLocalProcessing(true);
    const processedThisBatch: SelectedImage[] = [];

    try {
      for (const file of newFiles) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${t('aiWizard.errorInvalidFile')}: ${file.name}`);
          continue;
        }

        // Use centralized utility for compression
        const result = await compressImage(file, {
          maxWidth: 1600,
          maxHeight: 1600,
          quality: 0.7
        });

        processedThisBatch.push({
          base64: result.base64,
          mimeType: 'image/jpeg',
          previewUrl: result.previewUrl
        });
      }

      if (processedThisBatch.length > 0) {
        setLocalImages(prev => {
          const updated = [...prev, ...processedThisBatch];
          // Notify parent out of cycle
          setTimeout(() => {
            onImagesChange(updated.map(img => ({ base64: img.base64, mimeType: img.mimeType })));
          }, 0);
          return updated;
        });
      }
    } catch (error) {
      console.error('Processing error:', error);
      toast.error(t('aiWizard.errorProcessingFile'));
    } finally {
      setIsLocalProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
      // Reset input to allow selecting same file again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  };

  const handleGallerySelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  const handleRemove = (index: number) => {
    const updated = [...localImages];
    const [removed] = updated.splice(index, 1);

    if (removed.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }

    setLocalImages(updated);
    onImagesChange(updated.map(img => ({ base64: img.base64, mimeType: img.mimeType })));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('aiWizard.uploadPrescription')}</CardTitle>
        <CardDescription className="text-xs">
          {t('aiWizard.uploadPrescriptionHint')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleInputChange}
        />

        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-20 flex-col gap-2"
            onClick={handleCameraCapture}
            disabled={localImages.length >= MAX_FILES || isLocalProcessing}
          >
            <Camera className="w-6 h-6" />
            <span className="text-xs">{t('aiWizard.takePhoto')}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-20 flex-col gap-2"
            onClick={handleGallerySelect}
            disabled={localImages.length >= MAX_FILES || isLocalProcessing}
          >
            <Upload className="w-6 h-6" />
            <span className="text-xs">{t('aiWizard.selectFromGallery')}</span>
          </Button>
        </div>

        {isLocalProcessing && (
          <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground animate-pulse">
            <Loader2 className="w-4 h-4 animate-spin" />
            Processando imagens...
          </div>
        )}

        {localImages.length > 0 && !isLocalProcessing && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {localImages.map((img, index) => (
              <div key={index} className="relative group aspect-square">
                <img
                  src={img.previewUrl || `data:${img.mimeType};base64,${img.base64}`}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full rounded-lg border object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-1 -right-1 h-6 w-6 rounded-full shadow-lg"
                  onClick={() => handleRemove(index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileImage className="w-4 h-4" />
          <span>{t('aiWizard.supportedFormats')}</span>
        </div>
      </CardContent>
    </Card>
  );
}
